/**
 * InstagramBlock — "Comunidad en Instagram" del inicio.
 *
 * Trasladado desde `pages/Home.tsx` (CEC-260929-c2d8). La cuenta es fija:
 * @ceciluz_arte (perfil de arte). No depende del campo Instagram de
 * `site_settings`, que guarda el perfil de terapias. Solo se muestran las
 * fotos destacadas que tienen imagen, sin cuadros de relleno; miniaturas y
 * tarjeta van en una fila centrada (3 por fila en celular, 7 en escritorio).
 * Si una imagen falla al cargar, su cuadro se quita y la fila se reacomoda.
 */
import { useState } from 'react';
import { Sparkles } from 'lucide-react';

import { Container } from '../../components/layout/Container';
import { Image } from '../../components/ui/Image';
import { Skeleton } from '../../components/ui/Skeleton';
import { ErrorState } from '../../components/ui/ErrorState';
import { cn } from '../../lib/cn';
import { Routes } from '../../lib/routes';
import { useInstagramGallery, type InstagramGalleryItem } from './api';
import { StarDot } from './ornaments';

const INSTAGRAM_URL = 'https://www.instagram.com/ceciluz_arte';
const INSTAGRAM_HANDLE = '@ceciluz_arte';
const MAX_FOTOS = 6;

// Ancho de cada miniatura: 3 por fila en celular y 7 en escritorio (gap-4).
const ANCHO_CELDA = 'w-[calc((100%-2*var(--space-4))/3)] lg:w-[calc((100%-6*var(--space-4))/7)]';

function imagenDe(item: InstagramGalleryItem): string {
  return item.cover_url ?? item.media_urls?.[0] ?? '';
}

export function InstagramBlock() {
  const { data, isLoading, isError, error, refetch } = useInstagramGallery();
  // Ids de las fotos cuya imagen falló al cargar: no se muestran.
  const [fallidas, setFallidas] = useState<ReadonlySet<string>>(() => new Set());

  const items = (data ?? [])
    .filter((item) => imagenDe(item) !== '' && !fallidas.has(item.id))
    .slice(0, MAX_FOTOS);

  const ocultarFallida = (id: string) => setFallidas((prev) => new Set([...prev, id]));

  return (
    <section aria-labelledby="home-instagram-title" className="bg-cream-100 py-14 lg:py-20">
      <Container>
        <div className="mb-10 flex flex-col items-center gap-2 text-center">
          <h2
            id="home-instagram-title"
            className="inline-flex items-center gap-3 font-display text-h3 uppercase tracking-[0.18em] text-primary-700"
          >
            <StarDot className="text-sm" />
            Comunidad en Instagram
            <StarDot className="text-sm" />
          </h2>
          <p className="text-body text-primary-900/70">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary-900 transition-colors duration-2 ease-out"
            >
              {INSTAGRAM_HANDLE}
            </a>
          </p>
        </div>

        {isLoading && (
          <div className="grid grid-cols-3 gap-4 lg:grid-cols-7">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} height={140} rounded="md" />
            ))}
          </div>
        )}

        {isError && (
          <ErrorState
            title="No se pudieron cargar las publicaciones"
            error={error}
            onRetry={() => void refetch()}
          />
        )}

        {!isLoading && !isError && (
          <div className="flex flex-wrap items-start justify-center gap-4">
            {/* Hasta 6 miniaturas, solo las que tienen imagen */}
            {items.map((item: InstagramGalleryItem) => (
              <a
                key={item.id}
                href={Routes.galleryItem(item.slug)}
                aria-label={`Ver ${item.title}`}
                className={cn(
                  'group relative aspect-square overflow-hidden rounded-md',
                  ANCHO_CELDA,
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-cream-100',
                )}
              >
                <Image
                  src={imagenDe(item)}
                  alt={item.title}
                  width={320}
                  height={320}
                  ratio="1:1"
                  onError={() => ocultarFallida(item.id)}
                  className="h-full w-full object-cover transition-transform duration-3 ease-out group-hover:scale-105"
                />
              </a>
            ))}

            {/* CTA card — en escritorio ocupa una celda de la fila; sola, se centra */}
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                'flex w-full flex-col justify-center gap-3 rounded-md p-5',
                'bg-gradient-to-br from-primary-900 via-primary-800 to-primary-vivid',
                'text-cream-100 shadow-elev1',
                'transition-transform duration-2 ease-out hover:-translate-y-0.5',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-cream-100',
                items.length > 0 ? 'lg:w-[calc((100%-6*var(--space-4))/7)]' : 'max-w-md',
              )}
            >
              <p className="font-display text-body text-cream-50">
                Únete a nuestra comunidad <Sparkles className="inline h-4 w-4 text-gold-500" />
              </p>
              <p className="text-caption text-cream-100/80">
                Inspiración diaria, eventos y mensajes para tu alma.
              </p>
              <span className="mt-2 inline-flex items-center justify-center rounded-md border border-cream-100/60 px-3 py-2 text-button uppercase tracking-[0.06em] text-cream-50 hover:bg-cream-100/10 transition-colors duration-2 ease-out">
                Seguir en Instagram
              </span>
            </a>
          </div>
        )}
      </Container>
    </section>
  );
}
